import { useMemo, useState } from 'react';
import { ClassNames } from '@emotion/react';
import PropTypes from 'prop-types';
import {
  AlertCircle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  RotateCcw,
  Upload,
} from 'lucide-react';
import { tyreManagementApi } from '../api/tyreManagementApi';
import useTyreMutation from '../hooks/useTyreMutation';
import {
  downloadTyreImportTemplate,
  parseTyreWorkbook,
  validateTyreRows,
} from '../lib/tyreBulkUpload';
import FileUpload from './FileUpload';
import { Button } from './ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

const MAX_ROWS = 1000;
const PREVIEW_ROWS = 50;
const CREATE_BATCH_SIZE = 100;

const statusStyles = {
  ready: 'bg-sky-100 text-sky-800',
  invalid: 'bg-red-100 text-red-800',
  importing: 'bg-amber-100 text-amber-800',
  created: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-red-100 text-red-800',
};

const statusLabels = {
  ready: 'Ready',
  invalid: 'Needs attention',
  importing: 'Creating…',
  created: 'Created',
  failed: 'Failed',
};

const BulkTyreUploadDialog = ({ open, onOpenChange, sites, onComplete }) => {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [fileError, setFileError] = useState('');
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importTotal, setImportTotal] = useState(0);
  const bulkCreateMutation = useTyreMutation(tyreManagementApi.bulkCreateTyres);

  const counts = useMemo(() => rows.reduce((summary, row) => ({
    ...summary,
    [row.status]: (summary[row.status] || 0) + 1,
  }), {}), [rows]);
  const importableCount = (counts.ready || 0) + (counts.failed || 0);
  const finished = rows.length > 0 && (counts.created || 0) === rows.length;

  const reset = () => {
    setFile(null);
    setRows([]);
    setFileError('');
    setParsing(false);
    setImporting(false);
    setProgress(0);
    setImportTotal(0);
  };

  const handleOpenChange = (nextOpen) => {
    if (importing) return;
    onOpenChange(nextOpen);
    if (!nextOpen) reset();
  };

  const handleFile = async (selectedFile) => {
    setFile(selectedFile);
    setRows([]);
    setFileError('');
    setProgress(0);
    setImportTotal(0);
    if (!selectedFile) return;
    if (selectedFile.size > 5 * 1024 * 1024) {
      setFileError('The workbook cannot exceed 5 MB.');
      return;
    }

    setParsing(true);
    try {
      const parsedRows = parseTyreWorkbook(await selectedFile.arrayBuffer());
      if (!parsedRows.length) throw new Error('The Tyres sheet does not contain any inventory rows.');
      if (parsedRows.length > MAX_ROWS) throw new Error(`Import up to ${MAX_ROWS} tyres at a time.`);
      setRows(validateTyreRows(parsedRows, sites));
    } catch (error) {
      setFileError(error.message || 'The workbook could not be read.');
    } finally {
      setParsing(false);
    }
  };

  const importTyres = async () => {
    const candidates = rows.filter((row) => row.status === 'ready' || row.status === 'failed');
    if (!candidates.length) return;
    setImporting(true);
    setProgress(0);
    setImportTotal(candidates.length);
    const batches = Array.from(
      { length: Math.ceil(candidates.length / CREATE_BATCH_SIZE) },
      (_, index) => candidates.slice(index * CREATE_BATCH_SIZE, (index + 1) * CREATE_BATCH_SIZE),
    );
    let processed = 0;
    let createdAny = false;
    try {
      await batches.reduce((chain, batch) => chain.then(async () => {
        const batchRows = new Set(batch.map((row) => row.rowNumber));
        setRows((current) => current.map((row) => (
          batchRows.has(row.rowNumber) ? { ...row, status: 'importing', serverError: '' } : row
        )));
        try {
          const response = await bulkCreateMutation.mutateAsync({
            tyres: batch.map((row) => ({ row_number: row.rowNumber, ...row.payload })),
          });
          const resultByRow = new Map((response.results || []).map((result) => [result.row_number, result]));
          createdAny = createdAny || response.created_count > 0;
          setRows((current) => current.map((row) => {
            const result = resultByRow.get(row.rowNumber);
            if (!result) return row;
            return result.status === 'created'
              ? { ...row, status: 'created', created: result.tyre, serverError: '' }
              : { ...row, status: 'failed', serverError: (result.errors || []).join(' ') || 'The server rejected this row.' };
          }));
        } catch (error) {
          setRows((current) => current.map((row) => (
            batchRows.has(row.rowNumber) ? { ...row, status: 'failed', serverError: error.message } : row
          )));
        }
        processed += batch.length;
        setProgress(Math.round((processed / candidates.length) * 100));
      }), Promise.resolve());
      if (createdAny) await onComplete();
    } finally {
      setImporting(false);
    }
  };

  const hasInvalidRows = Boolean(counts.invalid);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-[calc(100%-1.5rem)] max-w-5xl">
        <DialogHeader>
          <div className="flex items-center gap-3.5">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-sky-200 bg-sky-50 text-sky-700 shadow-sm">
              <FileSpreadsheet className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle>Bulk upload tyres</DialogTitle>
              <DialogDescription>
                Turn invoice or purchase-order line items into inventory records with a reviewed Excel import.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-5">
          <div className="grid overflow-hidden rounded-xl border border-slate-200 bg-white lg:grid-cols-[0.8fr_1.2fr]">
            <div className="border-b border-slate-200 bg-slate-950 p-5 text-white lg:border-b-0 lg:border-r">
              <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-sky-300">Inventory manifest</p>
              <h3 className="mb-0 mt-2 text-lg font-extrabold">Start with the tyre template</h3>
              <p className="mb-0 mt-2 text-sm leading-6 text-slate-300">
                Copy rows from the supplier invoice or PO, keep one tyre per row, then upload the completed workbook.
              </p>
              <Button type="button" variant="outline" className="mt-4 border-slate-600 bg-slate-900 text-white hover:border-sky-400 hover:bg-slate-800" onClick={downloadTyreImportTemplate}>
                <Download className="h-4 w-4" /> Download Excel template
              </Button>
            </div>
            <div className="p-5">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-extrabold text-slate-900">Upload completed workbook</p>
                  <p className="mt-1 text-xs text-slate-500">Excel .xlsx or .xls · up to {MAX_ROWS} tyre rows</p>
                </div>
                {parsing && <Loader2 className="h-5 w-5 animate-spin text-sky-700" aria-label="Reading workbook" />}
              </div>
              <FileUpload
                id="tyre-inventory-workbook"
                file={file}
                onChange={handleFile}
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                hint="Excel .xlsx or .xls up to 5 MB"
              />
              {fileError && (
                <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {fileError}
                </div>
              )}
            </div>
          </div>

          {rows.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-extrabold text-slate-900">Review {rows.length} tyre rows</p>
                  <p className="mt-0.5 text-xs text-slate-500">Nothing is created until you confirm the import.</p>
                </div>
                <div className="flex flex-wrap gap-2 text-xs font-bold">
                  {counts.ready > 0 && <span className="rounded-full bg-sky-100 px-2.5 py-1 text-sky-800">{counts.ready} ready</span>}
                  {counts.invalid > 0 && <span className="rounded-full bg-red-100 px-2.5 py-1 text-red-800">{counts.invalid} need attention</span>}
                  {counts.created > 0 && <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-emerald-800">{counts.created} created</span>}
                  {counts.failed > 0 && <span className="rounded-full bg-red-100 px-2.5 py-1 text-red-800">{counts.failed} failed</span>}
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Row</TableHead>
                    <TableHead>Serial / tyre ID</TableHead>
                    <TableHead>Tyre</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.slice(0, PREVIEW_ROWS).map((row) => {
                    const siteName = sites.find((site) => String(site.id) === String(row.values.site))?.name || row.values.site || '—';
                    const issue = row.serverError || row.errors?.join(' · ');
                    return (
                      <TableRow key={row.rowNumber} className={row.status === 'invalid' || row.status === 'failed' ? 'bg-red-50/40' : ''}>
                        <TableCell className="font-mono text-xs text-slate-500">{row.rowNumber}</TableCell>
                        <TableCell>
                          <p className="font-bold text-slate-900">{row.values.serial_number || 'Missing serial'}</p>
                          <p className="mt-0.5 text-xs text-slate-500">{row.values.tyre_id || 'ID generated after creation'}</p>
                        </TableCell>
                        <TableCell>
                          <p className="font-semibold text-slate-800">{[row.values.brand, row.values.model].filter(Boolean).join(' ') || '—'}</p>
                          <p className="mt-0.5 text-xs text-slate-500">{row.values.size || '—'}</p>
                        </TableCell>
                        <TableCell>{siteName}</TableCell>
                        <TableCell className="max-w-[280px]">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${statusStyles[row.status]}`}>
                            {statusLabels[row.status]}
                          </span>
                          {issue && <p className="mt-1.5 text-xs leading-5 text-red-700">{issue}</p>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {rows.length > PREVIEW_ROWS && (
                <p className="border-t border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500">
                  Showing the first {PREVIEW_ROWS} rows. All {rows.length} rows will be imported.
                </p>
              )}
            </div>
          )}

          {(importing || progress > 0) && (
            <div className="rounded-xl border border-sky-200 bg-sky-50/70 p-4" role="status">
              <div className="mb-2 flex items-center justify-between gap-4 text-sm">
                <span className="font-bold text-slate-900">{importing ? 'Creating tyre inventory…' : 'Bulk upload processed'}</span>
                <span className="font-mono text-sm font-extrabold text-sky-800">{progress}%</span>
              </div>
              <div
                className="h-2.5 overflow-hidden rounded-full bg-sky-100 ring-1 ring-inset ring-sky-200"
                role="progressbar"
                aria-label="Bulk tyre upload progress"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={progress}
              >
                <ClassNames>
                  {({ css }) => (
                    <div className={`h-full rounded-full bg-sky-600 transition-[width] duration-300 ease-out ${css({ width: `${progress}%` })}`} />
                  )}
                </ClassNames>
              </div>
              <p className="mb-0 mt-2 text-xs text-slate-600">
                {importing ? 'Validated rows are being created in optimized server batches.' : 'Review the row results above before closing.'}
              </p>
            </div>
          )}

          {hasInvalidRows && (
            <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
              Correct the rows marked “Needs attention” in Excel, then upload the workbook again. No records have been created.
            </div>
          )}
          {finished && (
            <div className="flex items-center gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-800">
              <CheckCircle2 className="h-5 w-5 shrink-0" /> All {rows.length} tyres were created and added to inventory as Available.
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <div className="flex w-full flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-slate-500">
              New records remain unfitted until they are assigned from the vehicle workspace.
            </p>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" disabled={importing} onClick={() => handleOpenChange(false)}>
                {finished ? 'Done' : 'Cancel'}
              </Button>
              {!finished && (
                <Button type="button" disabled={!importableCount || hasInvalidRows || importing || parsing} onClick={importTyres}>
                  {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : counts.failed ? <RotateCcw className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
                  {importing
                    ? `Creating ${importTotal} tyres…`
                    : hasInvalidRows
                      ? `Fix ${counts.invalid} rows to continue`
                      : counts.failed
                      ? `Retry ${counts.failed} failed`
                      : `Create ${importableCount || ''} tyres`.trim()}
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

BulkTyreUploadDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  sites: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), name: PropTypes.string })).isRequired,
  onComplete: PropTypes.func.isRequired,
};

export default BulkTyreUploadDialog;
